import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { trackingTrack as trackEvent } from '@/core/api/generated/tracking/tracking';
import { isTrackingAllowed } from '@/core/ui/CookieBanner';
import { getOrCreateClientId } from '@/core/identity';

let deviceFingerprint: string | null = null;
let fpPromise: Promise<string> | null = null;

// Attribution data interface for registration
export interface AttributionData {
  clientId: string;
  deviceFingerprint: string;
  signupFbclid: string | null;
  signupGclid: string | null;
  signupGbraid: string | null;
  signupWbraid: string | null;
  signupTtclid: string | null;
  signupMsclkid: string | null;
  signupUtmSource: string | null;
  signupUtmMedium: string | null;
  signupUtmCampaign: string | null;
  signupUtmTerm: string | null;
  signupUtmContent: string | null;
  signupLandingPage: string | null;
  signupReferrer: string | null;
  referralCode: string | null;
}

// Interface for stored click ID with timestamp for expiration
interface StoredClickId {
  value: string;
  timestamp: number;
}

// Click ID expiration times in milliseconds (per platform guidelines)
const CLICK_ID_EXPIRY: Record<string, number> = {
  fbclid: 7 * 24 * 60 * 60 * 1000,     // 7 days (Meta)
  gclid: 90 * 24 * 60 * 60 * 1000,     // 90 days (Google Ads)
  gbraid: 90 * 24 * 60 * 60 * 1000,    // 90 days (Google Ads - iOS)
  wbraid: 90 * 24 * 60 * 60 * 1000,    // 90 days (Google Ads - web)
  ttclid: 7 * 24 * 60 * 60 * 1000,     // 7 days (TikTok)
  msclkid: 90 * 24 * 60 * 60 * 1000,   // 90 days (Microsoft/Bing)
};

// Store click ID with timestamp
function storeClickId(param: string, value: string): void {
  const data: StoredClickId = { value, timestamp: Date.now() };
  localStorage.setItem(param, JSON.stringify(data));
}

// Get click ID with expiration check
function getClickId(param: string): string | null {
  const stored = localStorage.getItem(param);
  if (!stored) return null;
  
  try {
    const data: StoredClickId = JSON.parse(stored);
    const expiry = CLICK_ID_EXPIRY[param] || 30 * 24 * 60 * 60 * 1000; // default 30 days
    
    if (Date.now() - data.timestamp > expiry) {
      // Click ID expired, remove it
      localStorage.removeItem(param);
      return null;
    }
    
    return data.value;
  } catch {
    // Migration: if it's an old string format, treat as valid and update format
    storeClickId(param, stored);
    return stored;
  }
}

// Initialize fingerprinting
async function initializeFingerprint(): Promise<string> {
  // Check consent before generating fingerprint
  if (!isTrackingAllowed()) {
    return 'anonymous';
  }

  if (deviceFingerprint) {
    return deviceFingerprint;
  }

  if (!fpPromise) {
    fpPromise = (async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        deviceFingerprint = result.visitorId;
        localStorage.setItem('device_fingerprint', deviceFingerprint);
        return deviceFingerprint;
      } catch (error) {
        // Fallback to a simple fingerprint if FingerprintJS fails
        const fallback = localStorage.getItem('device_fingerprint') || 
          `fp_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
        deviceFingerprint = fallback;
        localStorage.setItem('device_fingerprint', fallback);
        return fallback;
      }
    })();
  }

  return fpPromise;
}

// Detect and store click IDs from URL
function detectClickIds(): {
  fbclid?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
} {
  const urlParams = new URLSearchParams(window.location.search);
  const clickIds: Record<string, string> = {};

  const clickIdParams = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid'];
  
  clickIdParams.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      clickIds[param] = value;
      // Store with timestamp for expiration tracking
      storeClickId(param, value);
    } else {
      // Get from localStorage if not in URL (with expiration check)
      const stored = getClickId(param);
      if (stored) {
        clickIds[param] = stored;
      }
    }
  });

  return clickIds;
}

// Get all stored click IDs (with expiration check)
function getStoredClickIds(): {
  fbclid?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  ttclid?: string | null;
  msclkid?: string | null;
} {
  const clickIds: Record<string, string> = {};
  const clickIdParams = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid'];
  
  clickIdParams.forEach((param) => {
    // Use getClickId which checks expiration
    const stored = getClickId(param);
    if (stored) {
      clickIds[param] = stored;
    }
  });

  return clickIds;
}

// Detect and store UTM parameters from URL
function detectAndStoreUtmParams(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmParams.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      sessionStorage.setItem(param, value);
    }
  });
}

// Detect and store referral code from URL (?ref=)
function detectAndStoreReferralCode(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const ref = urlParams.get('ref');
  if (ref) {
    localStorage.setItem('referral_code', ref);
  }
}

// Store landing page (only on first visit)
function storeLandingPage(): void {
  if (!sessionStorage.getItem('landing_page')) {
    sessionStorage.setItem('landing_page', window.location.href);
  }
}

// Store original referrer (only on first visit)
function storeReferrer(): void {
  if (!sessionStorage.getItem('original_referrer') && document.referrer) {
    sessionStorage.setItem('original_referrer', document.referrer);
  }
}

// Get cookie value by name (for Facebook cookies)
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

// Get Facebook cookies for Meta CAPI matching
function getFacebookCookies(): { fbp: string | null; fbc: string | null } {
  return {
    fbp: getCookie('_fbp'),  // Facebook browser ID
    fbc: getCookie('_fbc'),  // Facebook click ID (includes timestamp)
  };
}

// Get all attribution data for registration
export async function getAttributionData(): Promise<AttributionData> {
  const fingerprint = await initializeFingerprint();
  const clickIds = getStoredClickIds();
  const clientId = getOrCreateClientId();
  
  return {
    // Anonymous profile identifier
    clientId,
    
    deviceFingerprint: fingerprint,
    
    // Click IDs (prefixed with signup for backend schema)
    signupFbclid: clickIds.fbclid || null,
    signupGclid: clickIds.gclid || null,
    signupGbraid: clickIds.gbraid || null,
    signupWbraid: clickIds.wbraid || null,
    signupTtclid: clickIds.ttclid || null,
    signupMsclkid: clickIds.msclkid || null,
    
    // UTM parameters
    signupUtmSource: sessionStorage.getItem('utm_source') || null,
    signupUtmMedium: sessionStorage.getItem('utm_medium') || null,
    signupUtmCampaign: sessionStorage.getItem('utm_campaign') || null,
    signupUtmTerm: sessionStorage.getItem('utm_term') || null,
    signupUtmContent: sessionStorage.getItem('utm_content') || null,
    
    // Landing page and referrer
    signupLandingPage: sessionStorage.getItem('landing_page') || null,
    signupReferrer: sessionStorage.getItem('original_referrer') || null,
    
    // Referral code
    referralCode: localStorage.getItem('referral_code') || null,
  };
}

// Get current store ID from localStorage (set by SessionContext)
function getCurrentStoreId(): string | null {
  // Primary: user's store preference
  const storeId = localStorage.getItem('user_store_preference');
  if (storeId) return storeId;
  
  // Fallback: auto-detected store
  const detectedStoreId = localStorage.getItem('detected_store_id');
  if (detectedStoreId) return detectedStoreId;
  
  return null;
}

// Main track function
export async function track(eventName: string, payload?: Record<string, unknown>, storeId?: string): Promise<void> {
  // Check consent before tracking
  if (!isTrackingAllowed()) {
    console.log('Tracking blocked: no consent');
    return;
  }

  try {
    // Get or initialize device fingerprint
    const fingerprint = await initializeFingerprint();
    
    // Get click IDs (from URL or localStorage)
    const clickIds = detectClickIds();
    const storedClickIds = getStoredClickIds();
    
    // Merge click IDs (URL takes precedence)
    const allClickIds = { ...storedClickIds, ...clickIds };
    
    // Get Facebook cookies for Meta CAPI matching
    const fbCookies = getFacebookCookies();

    // Prepare tracking data
    const trackingData = {
      eventName,
      payload: payload || {},
      deviceFingerprint: fingerprint,
      ...allClickIds,
      // Facebook cookies (critical for Meta CAPI matching quality)
      fbp: fbCookies.fbp,
      fbc: fbCookies.fbc,
      url: window.location.href,
      referrer: document.referrer || null,
      // Store ID for multi-store analytics
      storeId: storeId || getCurrentStoreId(),
    };

    // Use generated API function (fire and forget)
    trackEvent(trackingData as any).catch((error) => {
      console.error('Failed to track event:', error);
    });
  } catch (error) {
    console.error('Error in track function:', error);
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  // Initialize fingerprinting immediately
  initializeFingerprint();
  
  // Detect click IDs from initial URL
  detectClickIds();
  
  // Detect and store UTM parameters
  detectAndStoreUtmParams();
  
  // Detect and store referral code (?ref=)
  detectAndStoreReferralCode();
  
  // Store landing page (first page visited)
  storeLandingPage();
  
  // Store original referrer
  storeReferrer();
}
